import {marketingAssets} from './marketingAssets.js';

export interface FeatureScene {
  readonly image?: string;
  readonly imagePosition?: 'center' | 'right';
  readonly kind: 'still' | 'notes' | 'identity' | 'sequence' | 'palette' | 'balance' | 'choice';
  readonly lines?: readonly string[];
  readonly secondaryImage?: string;
  readonly stamp: string;
  readonly title: string;
  readonly video?: string;
}

const {coast} = marketingAssets;
const arrival = new URL('../assets/coastal-arrival-concept.png', import.meta.url).href;
const portrait = new URL('../assets/showcase-portrait.png', import.meta.url).href;
const harborCharacter = new URL('../assets/harbor-character-concept.png', import.meta.url).href;
const product = new URL('../assets/quick-video-fragrance.png', import.meta.url).href;
const campaign = new URL('../assets/fragrance-placement-concept.png', import.meta.url).href;

/** Each trio has a shared subject and a distinct reference, decision, and consequence. */
export const featureScenes: Readonly<Record<string, readonly FeatureScene[]>> = {
  characters: [
    {image: portrait, kind: 'still', stamp: 'CAST REFERENCE', title: 'Meet our protagonist'},
    {image: portrait, kind: 'identity', lines: ['Name: Ari', 'Role: harbor keeper', 'Trait: quietly determined'], stamp: 'CHARACTER SHEET', title: 'More than a face'},
    {image: harborCharacter, kind: 'still', stamp: 'CAST IN SCENE · CONCEPT', title: 'Ari recognizes the returning boat'}
  ],
  credits: [
    {kind: 'balance', lines: ['0 generation credits', 'Free account'], stamp: 'YOUR STARTING POINT', title: 'Plan at no cost'},
    {kind: 'notes', lines: ['✓ Project brief saved', '✓ Ari added to the cast', '✓ Three shots planned'], stamp: 'FREE PLANNING', title: 'Build before you generate'},
    {kind: 'choice', lines: ['Image & video generation', 'Media storage', 'Choose a paid membership'], stamp: 'PAID MEMBERSHIP', title: 'Generate when you upgrade'}
  ],
  elements: [
    {image: product, kind: 'still', stamp: 'PRODUCT REFERENCE', title: 'One signature fragrance'},
    {image: product, kind: 'identity', lines: ['Glass silhouette', 'Silver cap', 'Lavender glass'], stamp: 'REUSABLE ELEMENT', title: 'Keep its defining details'},
    {image: campaign, kind: 'still', stamp: 'PLACEMENT CONCEPT', title: 'From bottle to campaign'}
  ],
  gemini: [
    {image: new URL('../assets/video/coastal-arrival.jpg', import.meta.url).href, imagePosition: 'right', kind: 'sequence', stamp: 'REFERENCE CLIP', title: 'Study the harbor scene', video: new URL('../assets/video/coastal-arrival.mp4', import.meta.url).href},
    {kind: 'notes', lines: ['Observe: slow camera, quiet water', 'New premise: a midnight departure', 'Shot prompt: lanterns fade astern'], stamp: 'SCENARIO DRAFT · CONCEPT', title: 'Find an original direction'},
    {image: arrival, kind: 'still', stamp: 'IMAGE EXPLORATION · CONCEPT', title: 'Explore the next frame'}
  ],
  memory: [
    {image: coast, kind: 'still', stamp: 'EXPERIENCE', title: 'The boat never returned'},
    {kind: 'notes', lines: ['Ari remembers the departure', 'Promise: wait by the harbor', 'Unresolved: who is aboard?'], stamp: 'MEMORY RECORD · CONCEPT', title: 'A promise stays with him'},
    {image: harborCharacter, kind: 'identity', lines: ['Before: uncertainty', 'Trigger: the familiar boat', 'Now: hope → approach the dock'], stamp: 'EMOTIONAL CONTINUITY', title: 'Memory changes his response'}
  ],
  projects: [
    {kind: 'notes', lines: ['A boat returns after years away', 'Ari must decide to meet it', 'Blue dusk. Warm harbor lights.'], stamp: 'STORY BRIEF', title: 'The return'},
    {image: portrait, kind: 'identity', lines: ['Ari · harbor keeper', 'Goal: learn who returned', 'Conflict: fear of the answer'], stamp: 'YOUR CAST', title: 'Someone with a reason'},
    {kind: 'notes', lines: ['01 · Establish the coast', '02 · Follow the arriving boat', '03 · Ari waits at the dock'], stamp: 'SAVED PROJECT', title: 'An idea becomes a story'}
  ],
  'quick-video': [
    {kind: 'notes', lines: ['A fragrance reveal at golden hour', 'Start with the bottle', 'Finish on the warm product display'], stamp: 'ONE-CLIP IDEA', title: 'A moment for the brand'},
    {image: product, kind: 'identity', lines: ['Reference: fragrance', 'Motion: slow reveal', 'Format: short social clip'], stamp: 'CLIP DIRECTION', title: 'Set up the reveal'},
    {image: campaign, kind: 'sequence', lines: ['REVEAL', 'HERO'], stamp: 'CLIP CONCEPT', title: 'A campaign in one moment'}
  ],
  seedance: [
    {image: coast, kind: 'still', stamp: 'SOURCE · WIDE SHOT', title: 'The coast at dusk'},
    {kind: 'notes', lines: ['Follow the boat', 'Descend to water level', 'Keep the village lights warm'], stamp: 'CAMERA DIRECTION', title: 'Move into the scene'},
    {image: arrival, kind: 'sequence', lines: ['00:00', '00:05'], stamp: 'OUTCOME CONCEPT', title: 'Now alongside the boat'}
  ],
  storyboards: [
    {image: coast, kind: 'still', stamp: 'SHOT 01 · ESTABLISH', title: 'A quiet harbor at dusk'},
    {image: arrival, kind: 'still', stamp: 'SHOT 02 · DEVELOP', title: 'A boat breaks the silence'},
    {image: harborCharacter, kind: 'still', stamp: 'SHOT 03 · REVEAL', title: 'Someone has been waiting'}
  ],
  style: [
    {kind: 'palette', lines: ['Indigo sea', 'Violet dusk', 'Amber lights'], stamp: 'PROJECT PALETTE', title: 'Choose a visual language'},
    {kind: 'notes', lines: ['Low camera, close to the water', 'Amber reflections on the wake', 'Carry the indigo into shadows'], stamp: 'SCENE DIRECTION', title: 'Apply it to the arrival'},
    {image: arrival, kind: 'still', stamp: 'LOOK CONCEPT', title: 'The brief, made visible'}
  ],
  takes: [
    {image: coast, kind: 'choice', lines: ['Take A · Wide harbor', 'Take B · Close boat'], secondaryImage: arrival, stamp: 'COMPARE COMPOSITIONS', title: 'Which tells the story?'},
    {image: arrival, kind: 'still', stamp: 'SELECTED TAKE · CONCEPT', title: 'Choose the closer arrival'},
    {image: arrival, kind: 'identity', lines: ['The return / Shot 02', 'Selected: close boat', 'Ready to find again'], stamp: 'ORGANIZED ASSET', title: 'A result with a home'}
  ]
};
